import { FC } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { InternalOverviewResponse } from '../../types/internal';
import { InternalKPICard, InternalChartCard, InternalSectionHeader } from './index';
import { formatCurrency, formatNumber, tooltipStyle } from '../../utils/formatters';

interface InternalPaymentSectionProps {
    revenue: InternalOverviewResponse['revenue'];
    periodLabel: string;
}

export const InternalPaymentSection: FC<InternalPaymentSectionProps> = ({ revenue, periodLabel }) => {
    const revenueTrendData = (revenue.trend || []).map(item => ({
        month: item.month,
        revenue: item.revenue,
    }));

    const revenueByPlanData = (revenue.byPlan || []).filter(p => p.revenue > 0).slice(0, 5);

    return (
        <div className="space-y-4">
            <section>
                <InternalSectionHeader title="Payments & Growth" />
                <div className="grid grid-cols-5 gap-3">
                    <InternalKPICard label="Total Payments" value={formatCurrency(revenue.total ?? 0)} sublabelText="All time" primary tooltip="Total accumulated payments from all time." />
                    <InternalKPICard label={`Payments (${periodLabel})`} value={formatCurrency(revenue.inRange ?? 0)} sublabelText="Selected period" tooltip="Payments generated within the selected date range." />
                    <InternalKPICard label="Payments Today" value={formatCurrency(revenue.today ?? 0)} sublabelText="Current day" tooltip="Payments generated since midnight today." />
                    <InternalKPICard label="This Week" value={formatCurrency(revenue.thisWeek ?? 0)} sublabelText="Last 7 days" tooltip="Payments generated in the last 7 days." />
                    <InternalKPICard label="Total Sales" value={formatNumber(revenue.paymentCount ?? 0)} sublabelText="Transactions" tooltip="Total number of successful payment transactions." />
                </div>
            </section>

            <section className="grid grid-cols-3 gap-3">
                {/* Payments Trend */}
                <InternalChartCard title="Payments Trend" subtitle="Monthly performance" className="col-span-2" description="Monthly payment performance over time.">
                    <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={revenueTrendData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#171717" stopOpacity={0.1} />
                                        <stop offset="95%" stopColor="#171717" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#e5e5e5" strokeDasharray="2 4" vertical={false} />
                                <XAxis dataKey="month" tick={{ fontSize: 9, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fontSize: 9, fill: '#a3a3a3' }} axisLine={false} tickLine={false} />
                                <Tooltip contentStyle={tooltipStyle} />
                                <Area type="monotone" dataKey="revenue" stroke="#171717" strokeWidth={1.5} fillOpacity={1} fill="url(#colorRev)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </InternalChartCard>

                {/* Payments by Plan */}
                <InternalChartCard title="Payments by Plan" subtitle="Breakdown by subscription" description="Payment distribution across different subscription plans.">
                    <div className="space-y-2">
                        {revenueByPlanData.length > 0 ? revenueByPlanData.map((p, i) => (
                            <div key={i} className="flex items-center justify-between text-xs">
                                <span className="text-neutral-600 truncate max-w-[100px]">{p.plan || 'Unknown'}</span>
                                <span className="font-medium text-neutral-900">{formatCurrency(p.revenue)}</span>
                            </div>
                        )) : <p className="text-xs text-neutral-400">No data</p>}
                    </div>
                    <div className="mt-4 pt-3 border-t border-neutral-100 flex justify-between text-xs">
                        <span className="text-neutral-500">Total</span>
                        <span className="font-semibold text-neutral-900">{formatCurrency(revenue.total ?? 0)}</span>
                    </div>
                </InternalChartCard>
            </section>
        </div>
    );
};
